import { useCallback, useState } from "react";
import { Stack, StackProps } from "~/components/ui/stack";
import { ResponseButton } from "~/components/common/responseButton";
import { ApproveCommentsDrawer } from "./ApproveCommentsDrawer";
import { DeclineCommentsSheet } from "./DeclineCommentsDrawer";
import { useOutOfAreaActivationQuery } from "~/pages/requests/hooks";
import { useNavigate } from "react-router-dom";
import { captureException } from "@sentry/react";

type ApprovalActionButtonsProps = {
  activationId?: string;
  requestIds: Array<string>;
  sx?: StackProps["sx"];
  onSuccess?: () => void;
  approveWithChangesLink?: string;
  approveLabel?: string | null;
  approveWithChangesLabel?: string | null;
  approveWithChangesDisabled?: boolean;
  declineLabel?: string | null;
  onApproveClick?: () => void;
};

const defaultLink = "/out-of-area-activation-approvals/availability";

export const ApprovalActionButtons = ({
  activationId,
  requestIds,
  sx,
  onSuccess,
  approveWithChangesLink = defaultLink,
  onApproveClick: _onApproveClick,
  approveLabel = "Approve",
  approveWithChangesLabel = "Approve with changes",
  approveWithChangesDisabled,
  declineLabel = "Decline",
}: ApprovalActionButtonsProps) => {
  const navigate = useNavigate();
  const [isApproveDrawerOpen, setIsApproveDrawerOpen] = useState(false);
  const [isDeclineDrawerOpen, setIsDeclineDrawerOpen] = useState(false);

  const onApproveClick =
    _onApproveClick || useCallback(() => setIsApproveDrawerOpen(true), []);

  const onDeclinePress = useCallback(() => setIsDeclineDrawerOpen(true), []);

  const closeApproveDrawer = useCallback(
    () => setIsApproveDrawerOpen(false),
    []
  );
  const closeDeclineDrawer = useCallback(
    () => setIsDeclineDrawerOpen(false),
    []
  );

  const { refetch, isRefetching } = useOutOfAreaActivationQuery({
    activationId,
    enabled: false,
  });

  const handleApproveWithChanges = useCallback(async () => {
    if (requestIds.length === 1 && activationId) {
      try {
        const { data } = await refetch();

        const hasDeploymentRoles =
          data?.body && data.body.deployments.flatMap(d => d.roles)?.length > 0;

        // skip select role screen if request has no deployment roles
        if (!hasDeploymentRoles || approveWithChangesLink !== defaultLink) {
          navigate(
            `${approveWithChangesLink}?requestIds=${requestIds}&roleIds=""`
          );
          return;
        }
      } catch (err) {
        captureException(err);
      }
    }

    navigate(
      `/out-of-area-activation-approvals/roles?requestIds=${requestIds}&activationId=${activationId}`
    );
  }, [requestIds, activationId]);

  return (
    <>
      <Stack gap="medium" direction={{ xs: "column", md: "row" }} sx={sx}>
        {approveLabel !== null ? (
          <ResponseButton
            fullWidth
            background="positiveLow"
            onClick={onApproveClick}
            disabled={isRefetching}
          >
            {approveLabel}
          </ResponseButton>
        ) : null}

        {approveWithChangesLabel !== null ? (
          <ResponseButton
            fullWidth
            background="info"
            onClick={handleApproveWithChanges}
            busy={isRefetching}
            disabled={approveWithChangesDisabled}
          >
            {approveWithChangesLabel}
          </ResponseButton>
        ) : null}

        {declineLabel !== null ? (
          <ResponseButton
            fullWidth
            background="critical"
            onClick={onDeclinePress}
            disabled={isRefetching}
          >
            {declineLabel}
          </ResponseButton>
        ) : null}
      </Stack>

      <ApproveCommentsDrawer
        open={isApproveDrawerOpen}
        requestIds={requestIds}
        onClose={closeApproveDrawer}
        onSuccess={onSuccess}
      />

      <DeclineCommentsSheet
        open={isDeclineDrawerOpen}
        requestIds={requestIds}
        onClose={closeDeclineDrawer}
        onSuccess={onSuccess}
      />
    </>
  );
};
